import { Component, Prop, Element, Event, EventEmitter, State, Host, h } from '@stencil/core';
import type { Attributes } from '../../utils/functions';
import { inheritAriaAttributes } from '../../utils/functions';

/**
 * Buttons serve as interactive components that perform a specified action when clicked. Buttons should be clear in their function and facilitate simple task completion for the user.
 */
@Component({
  tag: 'som-button',
  styleUrl: 'som-button.scss',
  scoped: true,
})
export class SomButton {

  // Callback when a mutation occured
  handleMutationCb;
  internalButtonEl;

  @Element() el: HTMLElement;

  /**
   * This boolean property indicates that the user cannot interact with the button. Specifies that a button should be disabled.
   */
  @Prop({ reflect: true }) disabled?: boolean;

  /**
   * The type of the button.
   */
  @Prop() type: 'submit' | 'reset' | 'button' = 'button';

  /**
   * This property determines the color of the button.
   */
  @Prop() color: 'primary' | 'secondary' | 'white' | 'nav' | 'gray' | 'danger' = 'gray';

  /**
   * This property determines the size of the button.
   */
  @Prop() size?: 'sm' | 'md' | 'lg' | 'block' = 'md';

  /**
   * This property determines which side of the button the icon should appear
   */
  @Prop() iconPosition?: 'left' | 'right' | 'only' | 'none' = 'none';

  /**
   * This property determines which icon to be placed. It should be used in conjunction with iconPosition.
   */
  @Prop() icon?: string;

  /**
   * This property determines the button style
   */
  @Prop() buttonType: 'default' | 'outline' | 'clear' = 'default';

  /**
   * This property is used to set a specific label for screen reader users. This is helpful when using an icon only button.
   */
  @Prop() buttonAriaLabel?: string = '';

  /**
   * This boolean is used to toggle the loading mode.
   */
  @Prop() loading?: boolean = false;

  /**
   * This property is used to read a message to screen reader users while the button is loading.
   */
  @Prop() loadingMessage?: string;

  // Observer is a state variable to be copied in dev.
  @State() observer: MutationObserver;  

  /**
   * This event is emitted when the button has focus.
   */
  @Event() somFocus!: EventEmitter<void>;

  /**
   * This event is emitted when the button loses focus.
   */
  @Event() somBlur!: EventEmitter<void>;

  private onFocus = () => {
    this.somFocus.emit();
  };

  private onBlur = () => {
    this.somBlur.emit();
  };

  clickHandler(e) {
    if(this.disabled || this.loading) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private inheritedAttributes: Attributes = {};

  // Any change to an attribute on `som-button` will be reflected on the internal button
  handleMutation(mutations) {
    // Disable observer while refresh is in action
    this.disconnectObserver();

    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName in this.inheritedAttributes) {
        this.internalButtonEl.setAttribute(mutation.attributeName, mutation.target.getAttribute(mutation.attributeName))
      }
    })

    // Re-enable observer
    this.connectObserver();
  }

  disconnectObserver() {
    if (this.observer)
      this.observer.disconnect();
  }

  connectObserver() {
    this.observer.observe(this.el, {
      attributes: true
    });
  }

  componentWillLoad() {
    const colors = ['primary', 'secondary', 'white', 'nav', 'gray', 'danger']
    if (!colors.includes(this.color)) {
      this.color = 'gray'
    }

    this.inheritedAttributes = inheritAriaAttributes(this.el);
    if (this.observer) {
      // Hot module replacement hack. Since Stencil's HMR doesn't call componentDidUnload,
      // the observer is not cleaned when hot reloaded. Since the observer is @State-decorated,
      // it gets copied to the new reloaded component. Thus, if it exists here, we can
      // disconnect if it was already observing.
      // References:
      //   https://stencil-worldwide.slack.com/archives/C79EANFL7/p1550850171105200
      //   https://github.com/ionic-team/stencil/issues/1316
      this.disconnectObserver();
    }

    this.handleMutationCb = this.handleMutation.bind(this);
    this.observer = new MutationObserver(this.handleMutationCb);    
  }

  componentDidLoad() {
    this.connectObserver();
  }

  disconnectedCallback() {
    this.disconnectObserver();
  }

  _getStyle() {
    let css = [`btn btn-${this.size}`];

    this.buttonType === 'default' ? css.push(`btn-${this.color}`) : null;
    this.buttonType === 'outline' ? css.push(`btn-outline-${this.color}`) : null;
    this.buttonType === 'clear' ? css.push(`btn-link-${this.color}`) : null;

    if (this.iconPosition !== 'none') {
      this.iconPosition.toLowerCase() === 'only' ? css.push(`btn-icon btn-${this.color}`) : css.push(`btn-icon-${this.iconPosition} btn-${this.color}`);
    }

    this.loading ? css.push('loading') : null;
    this.disabled ? css.push(`disabled`) : null;

    return css.join(' ');
  }

  getIcon() {
    return `<som-icon name="${this.icon}" display="block"></som-icon>`;
  }
  
  render() {
    const {
      inheritedAttributes,
    } = this;

    return (
      <Host>
        <button
        ref={(el) => { this.internalButtonEl = el}}
        class={this._getStyle()}
        type={this.type}
        aria-label={this.buttonAriaLabel || null}
        aria-disabled={this.disabled || this.loading ? 'true' : 'false'}
        tabindex="0"
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onClick={(e) => this.clickHandler(e)}
        {...inheritedAttributes}>
          <span class="sr-only" role="alert" aria-live="assertive">
            {this.loading ? this.loadingMessage : null}
          </span>
          <div class="spinner-container" style={this.loading ? { visibility: 'visible', display: 'block' } : { visibility: 'hidden', display: 'none' }}>
            <som-spinner></som-spinner>
          </div>        
          <div class="btn-content" style={this.loading ? { visibility: 'hidden' } : { visibility: 'visible' }}>
            {this.iconPosition === 'left' && <span class={`icon-${this.size} icon-left`} innerHTML={`<som-icon name="${this.icon}" display="block"></som-icon>`}></span>}
            {this.iconPosition === 'only' ? <span innerHTML={`<som-icon name="${this.icon}" display="block"></som-icon>`}></span> : <slot></slot>}
            {this.iconPosition === 'right' && <span class={`icon-${this.size} icon-right`} innerHTML={`<som-icon name="${this.icon}" display="block"></som-icon>`}></span>}
          </div>
        </button>
      </Host>
    );
  }
}